<template>
    <CompaniesFilters
        v-model="filters"
        :has-active-filters="hasActiveFilters"
        :company-type="companyType"
        @submit="applyFilters"
        @reset="resetFilters"
    />
    <v-data-table-server
        v-model="selected"
        :headers="headers"
        :items="items"
        :items-per-page-options="[10, 15, 50, 100]"
        :loading="isLoading"
        :options="options"
        :items-length="companiesCount"
        class="elevation-1 table-server-selected"
        data-cy="clients-table"
        show-select
        disable-sort
        color="primary"
        @update:options="onUpdateTableOptions"
    >
        <template v-slot:item.name="{item}">
            <span
                class="text-subtitle-2 text-primary text-decoration-underline cursor-pointer"
                @click="goToCompanyClientProfile(item.id)"
            >
                {{ item.name }}
            </span>
            <template v-if="item.priorityLevel && isCRMExtendedModule && !isAgency && !isTO2">
                <sup v-if="item.priorityLevel === 'VIP'" class="text-error">
                    {{ item.priorityLevel }}
                </sup>
                <v-icon v-else-if="item.priorityLevel === 'HIGH'" color="success" size="small">
                    mdi-arrow-up-thin
                </v-icon>
                <v-icon v-else-if="item.priorityLevel === 'LOW'" color="orange" size="small">
                    mdi-arrow-down-thin
                </v-icon>
            </template>
        </template>
        <template v-slot:item.manager="{item}">
            {{ `${item.manager?.firstName || ''} ${item.manager?.lastName || ''}` }}
        </template>
        <template v-slot:item.type="{item}">
            {{ item.type ? $t(`companyTypes.${item.type}`) : '' }}
        </template>
        <template v-slot:item.emails="{item}">
            {{ item.emails?.length ? item.emails.join('\n') : '' }}
        </template>
        <template v-slot:item.stage="{item}">
            {{ item.stage ? $t(`crmStage.${item.stage}`) : '' }}
        </template>
        <template v-slot:item.currentBalance="{item}">
            <span v-if="item.currentBalance" :class="{'text-success': item.currentBalance.amount > 0}">
                {{
                    item.currentBalance.currency
                        ? $filters.formatPrice(item.currentBalance)
                        : item.currentBalance.amount
                }}
            </span>
        </template>
        <template v-slot:item.active="{item}">
            <v-switch
                v-model="item.active"
                :disabled="dontAllowEdit(item)"
                :label="item.active ? $t('active') : $t('inactive')"
                color="primary"
                density="compact"
                hide-details
                :data-cy="`show-active-company-${item.id}`"
                @update:model-value="changeActive(item)"
            />
        </template>
    </v-data-table-server>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Vue, Watch} from 'vue-facing-decorator'
    import CompaniesFilters from '~/components/account/companies/CompaniesFilters.vue'
    import {clean, clone, isEqual} from '~/utils/helpers'
    import {initCompanyFilters} from '~/utils/types/account/companies/helpers'
    import type {CompanyFiltersType} from '~/utils/types/account/companies/types'

    @Component({
        components: {CompaniesFilters},
    })
    export class CompaniesTable extends Vue {
        @Prop({default: [], required: true}) companyType: string[]
        @Prop({default: 'CLIENT'}) relationship: string
        @Watch('selected')
        onChangeSelectedEmails() {
            const emails: string[] = this.selectedCompanies.map(obj => obj.emails).flat()
            this.changeSelectedEmails(emails)
            this.changeSelectedCompanies()
        }
        @Emit()
        changeSelectedEmails(emails: string[]): string[] {
            return emails
        }
        @Emit()
        changeSelectedCompanies(): number[] {
            return this.selected
        }

        selected: number[] = []
        options = {
            itemsPerPage: 10,
            page: 1,
        }
        filters: CompanyFiltersType = initCompanyFilters()

        async mounted(): Promise<void> {
            await this.$store.restored
        }

        goToCompanyClientProfile(id) {
            this.$router.push({name: 'company-client-profile', params: {id}})
        }

        async loadCompanies(): Promise<void> {
            const {itemsPerPage, page} = this.options
            const filters = clone(this.filters)
            clean(filters)

            await runtimeStore.loadCompanies({
                ...filters,
                relationship: this.relationship,
                shortResponse: false,
                type: filters.type ? filters.type : this.companyType,
                limit: itemsPerPage,
                offset: itemsPerPage * (page - 1),
            })
        }

        get selectedCompanies() {
            return this.items.filter(({id}) => this.selected.includes(id))
        }

        applyFilters(): void {
            this.initOptions()
            this.loadCompanies()
        }

        resetFilters(): void {
            this.filters = initCompanyFilters()
            this.initOptions()
            this.loadCompanies()
        }

        get hasActiveFilters(): boolean {
            return !isEqual(initCompanyFilters(), this.filters)
        }

        async changeActive(item) {
            const {id, active} = item
            try {
                await this.$api.companies.patch(id, {active})
                this.$toast.success(this.$t('data_updated'))
            } catch (e) {
                this.$toast.error(this.$t('data_update_fail'))
            } finally {
                await this.loadCompanies()
            }
        }

        dontAllowEdit(item): boolean {
            return false
        }

        get headers() {
            let headers = [
                {title: this.$t('company_name'), value: 'name'},
                {title: this.$t('company_alias'), value: 'alias'},
                {title: this.$t('company_code'), value: 'code'},
                {title: this.$t('company_type'), value: 'type'},
                {title: this.$t('country'), value: 'country'},
                {title: this.$t('city'), value: 'city'},
                {title: this.$t('phone'), value: 'phone', minWidth: '180px'},
                {title: this.$t('email'), value: 'emails'},
                {title: this.$t('group_of_contracts'), value: 'groupOfContracts'},
                {title: this.$t('manager'), value: 'manager'},
                {title: this.$t('stage'), value: 'stage'},
                {title: this.$t('registration'), value: 'date'},
                {title: this.$t('first_order_date'), value: 'firstOrderDate'},
                {title: this.$t('last_order_date'), value: 'lastOrderDate'},
                {title: this.$t('number_of_reservations'), value: 'ordersCount', align: 'end'},
                {title: this.$t('current_balance'), value: 'currentBalance', align: 'end'},
                {title: this.$t('status'), value: 'active', minWidth: '140px'},
            ]

            if (this.isTO2 || !this.isCRMExtendedModule) {
                headers = headers.filter(({value}) => value !== 'stage')
            }
            if (this.isAgency) {
                headers = headers.filter(({value}) => value !== 'groupOfContracts' && value !== 'stage')
            }

            return headers
        }

        get items() {
            return runtimeStore.companies.map(company => ({
                id: company.id,
                name: company.name[company.language] || company.name,
                alias: company.alias,
                code: company.companyCode,
                type: company.type,
                country: company.countryName,
                city: company.cityName,
                phone: company.phones.join('\n'),
                emails: company.emails,
                date: company.creationDate ? this.$filters.dateShortFormat(company.creationDate) : '',
                active: company.active,
                groupOfContracts: company.groupOfContracts,
                manager: company.manager,
                stage: company.stage,
                firstOrderDate: company.firstOrderDate ? this.$filters.dateShortFormat(company.firstOrderDate) : '',
                lastOrderDate: company.lastOrderDate ? this.$filters.dateShortFormat(company.lastOrderDate) : '',
                registrationDate: company.registrationDate
                    ? this.$filters.dateShortFormat(company.registrationDate)
                    : '',
                ordersCount: company.ordersCount,
                currentBalance: company.currentBalance,
                priorityLevel: company.priorityLevel,
                language: company.language,
            }))
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isTO2() {
            return authStore.isTO2
        }

        get isCRMExtendedModule() {
            return runtimeStore.CRMSettings?.extendedCrm || false
        }

        initOptions(): void {
            this.options = {page: 1, itemsPerPage: this.options.itemsPerPage}
        }

        onUpdateTableOptions(options): void {
            this.options = options
            this.loadCompanies()
        }

        get companiesCount(): number {
            return runtimeStore.companiesCount || 0
        }

        get isLoading(): boolean {
            return runtimeStore.loading
        }
    }

    export default CompaniesTable
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';
    :deep(div) {
        td:not(:first-of-type) {
            min-width: 120px;
            white-space: break-spaces;
        }
    }

    @media #{map.get(settings.$display-breakpoints, 'md-and-down')} {
        :deep(div) {
            tr {
                td:first-of-type,
                td:nth-of-type(2),
                th:first-of-type,
                th:nth-of-type(2) {
                    position: sticky;
                    border-right: none;
                    background: map.get(settings.$grey, 'lighten-5');
                }
                td:first-of-type,
                th:first-of-type {
                    left: 0;
                }
                td:nth-of-type(2),
                th:nth-of-type(2) {
                    left: 44px;
                }
            }
        }
    }
</style>
